import { useState, useCallback, useMemo, useEffect } from "react";
import {
  Form,
  Input,
  Button,
  Descriptions,
  Modal,
  Popconfirm,
  notification,
  Tooltip,
  Space,
  Alert,
  Select,
  Tag,
} from "antd";
import {
  DeleteOutlined,
  LockOutlined,
  LoginOutlined,
  UpCircleOutlined,
} from "@ant-design/icons";
import axios from "axios";
import { useHistory } from "react-router-dom";
import { format } from "date-fns";

import { useAuth } from "../../hooks/useAuth";
import firebase from "../../services/firebase";
import useSegment from "../../hooks/useSegment";
import { handleError, sleep } from "../../helpers";
import { SERVICE_LEVEL } from "../../constants";

const { Option } = Select;

const SummaryPane = ({ cluster, assignment }) => {
  const auth = useAuth();
  const db = firebase.firestore();
  const segment = useSegment(auth);
  const [form] = Form.useForm();
  const history = useHistory();

  const [deleteOpen, setDeleteOpen] = useState(false);
  const [deleting, setDeleting] = useState(false);

  const [resetting, setResetting] = useState(false);
  const [clusterActions, setClusterActions] = useState([]);

  const [selectedUpgradeVersion, setSelectedUpgradeVersion] = useState(null);

  const getActions = useCallback(() => {
    const fetch = async () => {
      try {
        const token = await auth.getIdToken();
        const resp = await axios.get(
          `${process.env.REACT_APP_FB_FUNCTIONS_DOMAIN}/actions/cluster/${cluster.id}`,
          {
            headers: {
              Authorization: `Bearer ${token}`,
            },
          }
        );

        const actions = resp?.data?.actions
          .filter((action) => Object.keys(action).length > 1)
          .sort((a, b) => {
            return b.created._seconds - a.created._seconds;
          });

        setClusterActions(actions);
      } catch (error) {
        handleError(error);
        setClusterActions([]);
      }
    };
    fetch();
  }, [auth, cluster.id]);

  useEffect(() => {
    const unsubscribe = db
      .collection("dashboards")
      .doc(auth.user.db.id)
      .onSnapshot((snapshot) => {
        getActions();
      });
    return () => unsubscribe();
  }, [db, auth, getActions]);

  const resetPassword = useCallback(() => {
    const fetch = async () => {
      try {
        const token = await auth.getIdToken();
        await axios.post(
          `${process.env.REACT_APP_FB_FUNCTIONS_DOMAIN}/assignments/${assignment.id}/resetpassword`,
          {},
          {
            headers: {
              Authorization: `Bearer ${token}`,
            },
          }
        );
        notification.success({
          message: "Password Reset",
          description:
            "Your database password reset has been requested. Please check your e-mail in a few minutes for your new password.",
        });
      } catch (error) {
        handleError(error);
      } finally {
        setResetting(false);
      }
    };
    fetch();
  }, [assignment, auth]);

  const handleResetPasswordClick = () => {
    // Segment
    segment.track("reset_database_password");

    setResetting(true);
    resetPassword();
  };

  const onFinish = async (values) => {
    if (
      !values.confirm_cluster_name ||
      values.confirm_cluster_name !== cluster.cluster_name
    ) {
      form.setFields([
        {
          name: "confirm_cluster_name",
          value: values.confirm_cluster_name,
          errors: ["Database name must match exactly!"],
        },
      ]);
      return;
    }

    try {
      setDeleting(true);

      await sleep(1000);

      const token = await auth.getIdToken();
      await axios.delete(
        `${process.env.REACT_APP_FB_FUNCTIONS_DOMAIN}/clusters/${cluster.id}`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );

      // Segment
      segment.track("database_delete");

      notification.success({
        message: "Database delete",
        description: "Your database is being deprovisioned.",
      });
      setDeleteOpen(false);
      form.resetFields();

      history.push("/dashboard");
    } catch (error) {
      handleError(error);
    } finally {
      setDeleting(false);
    }
  };

  const onFinishFailed = ({ values, errorFields, outOfDate }) => {
    // console.log("onFinishFailed", values, errorFields, outOfDate);
  };

  const handleDelete = () => {
    form.submit();
  };

  const handleCancel = () => {
    setDeleteOpen(false);
    form.resetFields();
  };

  const handleLoginClick = (cluster, username) => () => {
    // Segment
    segment.track("workbench_login");

    window.open(
      `${cluster.workbench_url}/workbench/login?username=${username}`
    );
  };

  const handleUpgrade = async () => {
    try {
      const version = cluster.upgrade_info.upgradeVersions.find(
        (version) => version.tag === selectedUpgradeVersion
      );
      const token = await auth.getIdToken();
      await axios.post(
        `${process.env.REACT_APP_FB_FUNCTIONS_DOMAIN}/clusters/${cluster.id}/upgrade`,
        { version },
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );

      // Segment
      segment.track("database_upgrade");

      notification.success({
        message: "Database Upgrade",
        description: "Database is being upgraded.",
      });
    } catch (error) {
      handleError(error);
    }
  };

  const upgradeInfo = useMemo(
    (_) => {
      if (cluster.upgrade_info) {
        return cluster.upgrade_info;
      }
      return null;
    },
    [cluster]
  );

  useEffect(
    (_) => {
      if (
        upgradeInfo &&
        upgradeInfo.upgradeAvailable &&
        upgradeInfo.upgradeVersions.length > 0
      ) {
        setSelectedUpgradeVersion(
          upgradeInfo.upgradeVersions[upgradeInfo.upgradeVersions.length - 1]
            .tag
        );
      }
    },
    [upgradeInfo]
  );

  const isUpgrading = useMemo(
    (_) => {
      return clusterActions.some((action) => {
        return action.type === "upgrade_cluster" && action.status === "pending";
      });
    },
    [clusterActions]
  );

  const lastUpgrade = useMemo(
    (_) => {
      return clusterActions.find((action) => {
        return action.type === "upgrade_cluster";
      });
    },
    [clusterActions]
  );

  const handleUpgradeVersionChange = (upgradeVersion) => {
    setSelectedUpgradeVersion(upgradeVersion);
  };

  return (
    <>
      <Space style={{ float: "right" }}>
        <Popconfirm
          key="reset"
          title={
            <>
              Your new auto-generated password will be e-mailed to you.
              <br />
              Are you sure you want to reset your database password?
            </>
          }
          onConfirm={handleResetPasswordClick}
          okText="Yes"
          cancelText="No"
          disabled={resetting}
        >
          <Button key="reset_password">
            <LockOutlined /> Reset Password
          </Button>
        </Popconfirm>
        {cluster.service_level === SERVICE_LEVEL.DEDICATED && (
          <Button
            icon={<DeleteOutlined />}
            onClick={(_) => setDeleteOpen(true)}
            danger
          >
            Delete
          </Button>
        )}
        <Tooltip
          title={
            <div style={{ padding: "5px 10px" }}>
              DB User: <strong>{assignment?.admin_username}</strong>
            </div>
          }
          color="magenta"
        >
          <div className={`button-login`}>
            <Button
              type="primary"
              htmlType="submit"
              size="medium"
              onClick={handleLoginClick(cluster, assignment?.admin_username)}
              disabled={assignment?.status !== "complete"}
              style={{
                border: 0,
                color: "#ffffff",
                borderRadius: "5px",
                overflow: "hidden",
                zIndex: "1",
                backgroundColor: "transparent",
                width: 100,
              }}
              block
            >
              <LoginOutlined /> Login
              <div className="button-login-fill"></div>
            </Button>
          </div>
        </Tooltip>
      </Space>
      <h2 style={{ fontWeight: 300, marginBottom: "15px" }}>Database Info</h2>
      {upgradeInfo &&
        upgradeInfo.upgradeAvailable &&
        upgradeInfo.upgradeVersions.length > 0 &&
        cluster.service_level !== SERVICE_LEVEL.FREE_SHARED && (
          <Alert
            message="Upgrade Available"
            description={
              !isUpgrading && (
                <>
                  Please select an upgrade version
                  <Select
                    value={selectedUpgradeVersion}
                    onChange={handleUpgradeVersionChange}
                    disabled={isUpgrading}
                    style={{
                      minWidth: 180,
                      marginLeft: 10,
                    }}
                  >
                    {upgradeInfo.upgradeVersions.map((version) => {
                      return (
                        <Option key={version.semVer} value={version.tag}>
                          <Tag
                            style={{
                              color: "#999999",
                            }}
                          >
                            Tag: {version.tag}
                          </Tag>
                        </Option>
                      );
                    })}
                  </Select>
                  {lastUpgrade && (
                    <div
                      style={{
                        fontSize: "14px",
                        backgroundColor:
                          lastUpgrade.status === "error"
                            ? "#ff4d4f22"
                            : "#ffffffcc",
                        padding: "6px 15px",
                        marginTop: "20px",
                        display: "block",
                        borderRadius: 5,
                        border:
                          lastUpgrade.status === "error"
                            ? "1px solid #ff4d4f33"
                            : "1px solid #ffffffcc",
                      }}
                    >
                      <i>
                        {`Last Attempt: ${format(
                          new Date(lastUpgrade.created._seconds * 1000),
                          "yyyy-MM-dd hh:mm aaa"
                        )}`}
                        <br />
                        {`Status: ${lastUpgrade.log} (${lastUpgrade.status})`}
                      </i>
                    </div>
                  )}
                </>
              )
            }
            type="info"
            action={
              isUpgrading ? (
                <Button type="primary" icon={<UpCircleOutlined />} loading>
                  Upgrading
                </Button>
              ) : selectedUpgradeVersion ? (
                <Popconfirm
                  key="reset"
                  title="Are you sure you want to perform an upgrade?"
                  onConfirm={handleUpgrade}
                  okText="Yes"
                  cancelText="No"
                >
                  <Button type="primary" icon={<UpCircleOutlined />}>
                    Upgrade
                  </Button>
                </Popconfirm>
              ) : (
                <Button type="primary" icon={<UpCircleOutlined />} disabled>
                  Upgrading
                </Button>
              )
            }
            style={{ marginBottom: 20 }}
            showIcon
          />
        )}
      <Descriptions column={1} size="small" bordered>
        <Descriptions.Item label="Hostname">
          {cluster?.hostname}
        </Descriptions.Item>
        {cluster.service_level === SERVICE_LEVEL.DEDICATED &&
          cluster.current_version && (
            <Descriptions.Item label="Current Version">
              {cluster?.current_version}
            </Descriptions.Item>
          )}
        {cluster.service_level === SERVICE_LEVEL.DEDICATED && (
          <>
            <Descriptions.Item label="Size / Type">
              {cluster?.cluster_size} / {cluster?.cluster_type}
            </Descriptions.Item>
            <Descriptions.Item label="Provider">
              {cluster?.cloud_provider}
            </Descriptions.Item>
          </>
        )}
        <Descriptions.Item label="Workbench">
          <a href={cluster?.workbench_url} target="_blank" rel="noreferrer">
            {cluster?.workbench_url}
          </a>
        </Descriptions.Item>
        <Descriptions.Item label="API Endpoint">
          <a
            href={`${cluster?.workbench_url}/${cluster?.cluster_name}/gpudb-0`}
            target="_blank"
            rel="noreferrer"
          >
            {cluster?.workbench_url}/{cluster?.cluster_name}/gpudb-0
          </a>
        </Descriptions.Item>
        <Descriptions.Item label="Postgres">
          {cluster?.workbench_url.replace("https://", "")}:{cluster?.postgres}
        </Descriptions.Item>
      </Descriptions>
      {deleteOpen && (
        <Modal
          title={<span style={{ color: "#ff4d4f" }}>Delete Database</span>}
          visible={deleteOpen}
          okText="Confirm"
          okButtonProps={{ danger: true }}
          onOk={handleDelete}
          confirmLoading={deleting}
          onCancel={handleCancel}
          style={{ border: "10px solid #ff4d4f" }}
          destroyOnClose
          centered
        >
          <p>
            This is irreversible. We will delete your database and all
            associated backups. All database data will be purged and
            irretrievable.
          </p>
          <p>
            Confirm by entering the name of your cluster below:
            <br />
            <strong style={{ color: "#ff4d4f" }}>{cluster.cluster_name}</strong>
          </p>
          <Form
            key={alert.id}
            form={form}
            layout="vertical"
            initialValues={{}}
            onFinish={onFinish}
            onFinishFailed={onFinishFailed}
          >
            <Form.Item label="Confirm cluster name" name="confirm_cluster_name">
              <Input placeholder="My cluster name" size="large" />
            </Form.Item>
          </Form>
        </Modal>
      )}
    </>
  );
};

export default SummaryPane;
